/**
 * Stage 33 — typed view of the Stage 32B/32C candidate API
 * (`backend/app/candidate/pair_router.py` + `pair_service.py`).
 *
 * Only fields the UI actually reads are typed; the envelope keeps the rest reachable
 * without `any`. Three separate score carriers — `trackA`, `anomaly`, `pairing` — are
 * modelled as three unrelated types on purpose: there is no combined-score type here,
 * and none may be introduced.
 */

import type { AnomalyExplanationBlock } from './cross-unit.reason-evidence'

/* ── envelope ─────────────────────────────────────────────────────────────── */

export type CandidateEnvelopeStatus = 'OK' | 'ERROR'

export interface CandidateEnvelope<T> {
  status: CandidateEnvelopeStatus
  errorCode: string | null
  component: string | null
  reason: string | null
  data: T | null
  meta?: Record<string, unknown>
}

/** Emitted by the Next proxy when the Stage 33 flag is off — never reaches the backend. */
export const CROSS_UNIT_DISABLED_CODE = 'CROSS_UNIT_SCENARIOS_DISABLED'

/* ── scores (three independent blocks) ────────────────────────────────────── */

export type ScoreLevel = string

export interface TrackAScore {
  modelFamily?: string
  modelRole?: string
  modelVersion?: string | null
  targetVersion?: string | null
  rawScore?: number | null
  displayedScore0to10?: number | null
  level?: ScoreLevel | null
  relativeDecile?: number | null
  frozenPercentile?: number | null
  status?: string | null
  scoreScope?: string | null
  cohortId?: string | null
  isProbability?: boolean
  isCombinedScore?: boolean
  productionScoreEligible?: boolean
  available?: boolean
  errorCode?: string | null
  reason?: string | null
  diagnosticOnly?: boolean
  scalesNotComparable?: boolean
  comparabilityNote?: string | null
  semantics?: Record<string, unknown>
}

/** Same wire shape as Track A; a distinct alias keeps the two from being interchanged. */
export type AnomalyScore = TrackAScore

export interface PairingStrength {
  matchScore: number | null
  importRank: number | null
  exportRank: number | null
  importCandidateCount?: number | null
  exportCandidateCount?: number | null
  mutualTop1?: boolean
  daysBetween?: number | null
  isProbability?: boolean
  isPhysicallyConfirmed?: boolean
}

export interface BaselineDiagnostic {
  trackA: TrackAScore | null
  anomaly: AnomalyScore | null
  scalesNotComparable?: boolean
  neverSubstitutesPrimary?: boolean
}

/* ── legs ─────────────────────────────────────────────────────────────────── */

export type LegFieldStatus = 'populated' | 'source_value_missing' | 'field_not_in_source'

export interface LegSummary {
  available: boolean
  resolutionStatus?: string
  sourceUid?: string | null
  shipmentId?: string | null
  unitType?: string | null
  wagonNumber?: string | null
  containerNumber?: string | null
  cargoEtsng?: string | null
  cargoGng?: string | null
  cargoName?: string | null
  departureCountry?: string | null
  destinationCountry?: string | null
  departureStation?: string | null
  destinationStation?: string | null
  principalDate?: string | null
  weightTons?: number | null
}

/** Full detail leg. Every non-identity field is accompanied by `fieldStatus[name]`. */
export interface LegDetail {
  available: boolean
  resolutionStatus?: string
  reason?: string | null
  direction?: string | null
  sourceUid?: string | null
  sourceYear?: number | null
  sourceFilename?: string | null
  sourceSheet?: string | null
  sourceRowNumber?: number | null
  sourceSnapshotVersion?: string | null
  fieldStatus?: Record<string, LegFieldStatus>
  [field: string]: unknown
}

/* ── list item ────────────────────────────────────────────────────────────── */

export type PairingStatus = 'EXACT' | 'INFERRED_ONE_TO_ONE' | 'AMBIGUOUS' | string
export type MembershipStatus =
  | 'group_representative'
  | 'existing_selected_link'
  | 'alternative_not_confirmed'
  | string

export interface CandidatePairItem {
  canonicalPairId: string
  stableCaseGroupId: string
  importSourceUid: string
  exportSourceUid: string
  exportDate: string | null
  exportYear: number | null
  pairingStatus: PairingStatus
  isGroupRepresentative: boolean
  isExistingSelected: boolean
  isConfirmedPairing: boolean
  membershipStatus: MembershipStatus
  pairing: PairingStrength
  scenario: {
    canonicalScenarioCode: string | null
    primaryDisplayScenarioId: number | null
    tz05Member?: boolean
    tz06Member?: boolean
  }
  trackA: TrackAScore
  anomaly: AnomalyScore
  history: { limitedHistory: boolean; historyScope?: string | null }
  importSummary: LegSummary
  exportSummary: LegSummary
  legsAvailable: boolean
  legsEnriched: boolean
  cluster: {
    stableCaseGroupId: string
    representativeStatus: string
    eligibleCandidateCount: number | null
    selectedCandidateCount: number | null
    ambiguousCandidateCount: number | null
  }
  lifecycle?: Record<string, unknown>
  warnings?: string[]
  baselineDiagnostic?: BaselineDiagnostic
}

export interface CandidatePagination {
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
  hasNext: boolean
  paginatedInDatabase?: boolean
  paginationMandatory?: boolean
}

export interface CandidatePairListData {
  items: CandidatePairItem[]
  pagination: CandidatePagination
  counts: { candidatePairCount: number; coverageScope?: string }
  semantics?: Record<string, unknown>
}

/* ── pair detail ──────────────────────────────────────────────────────────── */

export interface CandidatePairDetailData {
  identity: {
    canonicalPairId: string
    stableCaseGroupId: string
    importSourceUid: string
    exportSourceUid: string
    candidateFamily?: string | null
    catalogVersion?: string | null
    sourceSnapshotVersion?: string | null
    createdFromPipelineStage?: string | null
  }
  importLeg: LegDetail
  exportLeg: LegDetail
  legs: {
    available: boolean
    legsEnriched: boolean
    importSourceUid?: string
    exportSourceUid?: string
    importResolved?: boolean
    exportResolved?: boolean
    resolutionStatus?: string
    reason?: string
  }
  fieldAvailability: Record<string, unknown>
  sourceSnapshot: Record<string, unknown>
  dataLimitations: string[]
  pairing: PairingStrength & {
    pairingStatus: PairingStatus
    isExistingSelected: boolean
    isGroupRepresentative: boolean
    isConfirmedPairing: boolean
    representativeStatus: string
    matchScoreLabel?: string
    matchScoreIsProbability?: boolean
    physicallyConfirmed?: boolean
    importRetainedGroupSize?: number | null
    exportRetainedGroupSize?: number | null
    importTiesAtBest?: boolean
    exportTiesAtBest?: boolean
    equalScoreAlternativeCount?: number | null
    semantics?: Record<string, unknown>
  }
  scenario: {
    canonicalScenarioCode: string | null
    primaryDisplayScenarioId: number | null
    tz05RuleStrict?: boolean
    tz06RuleEligible?: boolean
    clusterMemberships?: Array<Record<string, unknown>>
    clusterPrimaryDisplayScenarioId?: number | null
  }
  trackA: TrackAScore
  anomaly: AnomalyScore
  explanations: CandidateExplanationsData
  /**
   * Stage 38 — quantitative evidence for the anomaly reason codes above: the feature that
   * fired, its value, and the baseline distribution over the model's own train population.
   * Absent when the evidence sidecar is not configured; `reasonCodes` stay authoritative.
   */
  anomalyExplanation?: AnomalyExplanationBlock | null
  history: {
    limitedHistory: boolean
    historyScope?: string | null
    exportYear?: number | null
    note?: string | null
  }
  baselineDiagnostic: BaselineDiagnostic
  cluster: {
    stableCaseGroupId: string
    representativeCanonicalPairId: string | null
    representativeStatus: string
    eligibleCandidateCount: number | null
    selectedCandidateCount: number | null
    ambiguousCandidateCount: number | null
    isPhysicalShipmentPair: boolean
    meaning?: string
  }
  lifecycle?: Record<string, unknown>
  warnings?: string[]
}

/* ── scores / explanations endpoints ──────────────────────────────────────── */

export interface CandidatePairScoresData {
  canonicalPairId: string
  found: boolean
  trackA: TrackAScore
  anomaly: AnomalyScore
  pairingStrength?: { note?: string }
  baselineDiagnostic: BaselineDiagnostic
  /** Structurally always null — the API carries no combined score. */
  combinedScore: null
  isCombinedScorePresent: false
  warnings?: string[]
}

export interface ShapFactor {
  feature: string
  contribution: number | null
}

export interface TrackAShapExplanation {
  modelVersion: string | null
  explanationType: string | null
  shapBaseValue: number | null
  shapSumPositive: number | null
  shapSumNegative: number | null
  topPositiveFactors: ShapFactor[]
  topNegativeFactors: ShapFactor[]
  featureSchemaVersion: string | null
  status: string | null
  scope: string | null
}

export interface AnomalyReasonsExplanation {
  modelVersion: string | null
  explanationType: string | null
  reasonCodes: string[]
  featureSchemaVersion: string | null
  status: string | null
  scope: string | null
}

export interface CandidateExplanationsData {
  canonicalPairId?: string
  trackAShap: TrackAShapExplanation | null
  anomalyReasons: AnomalyReasonsExplanation | null
  separateSections?: boolean
  unavailable?: { errorCode: string; reason: string } | null
}

/* ── clusters ─────────────────────────────────────────────────────────────── */

export interface CandidateClusterData {
  stableCaseGroupId: string
  groupStatus: string | null
  representativeCanonicalPairId: string | null
  representativeStatus: string
  representativePairingStatus?: string | null
  representativeIsExistingSelected?: boolean
  representativeIsConfirmed: boolean
  counts: {
    eligibleCandidateCount: number | null
    selectedCandidateCount: number | null
    ambiguousCandidateCount: number | null
    exactCandidateCount?: number | null
    alternativeCandidateCount?: number | null
    importLegCount?: number | null
    exportLegCount?: number | null
    limitedHistoryLinkCount?: number | null
  }
  scenario: {
    primaryDisplayScenarioId: number | null
    primaryCanonicalScenarioCode?: string | null
    hasTz05Membership?: boolean
    hasTz06Membership?: boolean
    tz05LinkCount?: number | null
  }
  trackA: {
    maxScore0to10: number | null
    maxScoreSourceCanonicalPairId: string | null
    maxRelativeDecile: number | null
    representativeScore0to10: number | null
    availableCount?: number | null
    modelVersion?: string
    averaged?: boolean
  }
  anomaly: {
    maxScore0to10: number | null
    maxScoreSourceCanonicalPairId: string | null
    maxLevel: string | null
    representativeScore0to10: number | null
    availableCount?: number | null
    modelVersion?: string
    averaged?: boolean
  }
  pairingStrength: { maxMatchScore: number | null; isProbability?: boolean }
  combinedScore: null
  dateRange?: { minExportDate: string | null; maxExportDate: string | null }
  representativePair?: CandidatePairItem | null
  alternativesRequirePagination?: boolean
  lifecycle?: Record<string, unknown>
  semantics?: Record<string, unknown>
}

export interface CandidateClusterPairsData {
  stableCaseGroupId: string
  items: CandidatePairItem[]
  pagination: CandidatePagination
  ordering?: { sort: string; contract: string }
  semantics?: Record<string, unknown>
}

/* ── coverage ─────────────────────────────────────────────────────────────── */

export interface CandidateCoverageData {
  coverageScope: string
  primaryMlObject: string
  catalog: Record<string, unknown>
  scenarios1to5: { byScenario?: Record<string, number>; total?: number; unchangedFromStage31?: boolean }
  scenarios6to7: {
    candidatePairCount: number
    tz05PrimaryPairCount: number
    tz06PrimaryPairCount: number
    exactCount: number
    inferredOneToOneCount: number
    ambiguousCount: number
    selectedPairCount: number
    ambiguousPairCount: number
    alternativePairCount: number
    representativePairCount: number
    limitedHistoryPairCount: number
    competitionClusterCount: number
    scenario6ClusterCount: number
    scenario7ClusterCount: number
    rejectedDiagnosticCount: number
    largestClusterPairCount: number
  }
  countLabels?: Record<string, string>
  models?: Record<string, unknown>
  levels?: Record<string, Record<string, number>>
  expectations?: { allMatch?: boolean; matches?: Record<string, boolean> }
}

/* ── list query ───────────────────────────────────────────────────────────── */

export const CANDIDATE_DEFAULT_PAGE_SIZE = 50
export const CANDIDATE_MAX_PAGE_SIZE = 200

export type CandidatePairSort =
  | 'track_a_desc'
  | 'anomaly_desc'
  | 'pairing_strength_desc'
  | 'export_date_desc'
  | 'canonical_pair_id'

export type CandidateClusterPairSort =
  | 'track_a_desc'
  | 'anomaly_desc'
  | 'pairing_strength_desc'
  | 'canonical_pair_id'

export interface CandidatePairQuery {
  page?: number
  pageSize?: number
  sort?: CandidatePairSort
  displayScenarioId?: 6 | 7
  pairingStatus?: PairingStatus
  isExistingSelected?: boolean
  isGroupRepresentative?: boolean
  stableCaseGroupId?: string
  trackAMin?: number
  trackAMax?: number
  trackALevel?: string
  trackARelativeDecile?: number
  anomalyMin?: number
  anomalyMax?: number
  /** Legacy single-level equality. Mutually exclusive with the two selectors below. */
  anomalyLevel?: string
  /**
   * Stage 33A. `true` selects every non-baseline anomaly level (the full anomalous
   * review subset), `false` selects baseline only. Resolved inside DuckDB before
   * pagination, so the returned total is the filtered population.
   */
  analystReviewEligible?: boolean
  /** Stage 33A. Explicit level set, e.g. `['elevated','high']`. */
  anomalyLevelIn?: string[]
  matchScoreMin?: number
  matchScoreMax?: number
  limitedHistory?: boolean
  exportYear?: number
  includeBaseline?: boolean
}
